import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Observable, of, take, from, EMPTY } from 'rxjs';
import { mergeMap, catchError, scan, map, switchMap, tap } from 'rxjs/operators';
import { Models } from '../../pipeline/models/global.models';
import { ConfirmationModalComponent } from '../../core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalCloseType } from '../../models';
import { NotificationService } from '../../services';
import { DialogService } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { navigateWithRefresh } from '../utils/router.util';
import { RouteUiService } from '../../pipeline/stores/ui/route-ui.service';
import { SpinnerService } from './spinner.service';
import { PhoneNumberPipe } from '../pipes';
import { PartnerNotesQuery } from '../state/partner-notes.query';
import { PartnerNote, PartnerNotesDataStore } from '../state/partner-notes.store';
import { showPartnersDrawer } from '../../pipeline/components/partners/utils/partners.utils';
import { addIconClassToModalHeader, isNullish } from '../utils';
import { AddPartnerModalComponent } from '../../pipeline/components/partners/components/add-partner-modal/add-partner-modal.component';
import { SessionQuery } from '../../core/state/session/session.query';

export type PartnerData = Models.PipelinePartner;

export interface PartnerLicense {
  licenseState?: string;
  licenseNumber?: string;
}

export enum InviteStatus {
  NotInvited = 'Not Yet Invited',
  InvitationSent = 'Invite Sent',
  AccountCreated = 'Account Created',
}

export const INVITE_ALREADY_CREATED_DETAIL = 'Invite not sent to partners who have already created their account';

export interface CoBrandingRequest {
  partnerGuid: string;
  loanOfficerNmls: string;
}

export interface AssignPartnerRequest {
  partnerGuid: string;
  fileDataId: number;
  loanOfficerNmlsId: string;
}

export interface ChangePartnerRequest {
  fileDataId: number;
  oldPartnerGuid: string;
  newPartnerGuid: string;
  loanOfficerNmlsId: string;
}

export interface BulkPartnersResponse {
  successful?: string[];
  failed?: string[];
}

// Progressive response shape emitted after each partner processed
export interface BulkPartnersProgress extends BulkPartnersResponse {
  processedPartners: number; // number of partners attempted so far
  totalPartners: number; // total eligible partners
  last?: PartnerResponse; // last raw response
  isComplete: boolean; // true when processed === total
}

export interface PartnerResponse {
  partnerGuid: string;
  successful: boolean;
  error?: string;
}

export interface EditPartnerData {
  firstName: string;
  lastName: string;
  loanOfficerNMLS: string;
  title: string;
  phone: string;
  email: string;
  website: string;
  licenses?: PartnerLicense[];
  company: string;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZipCode: string;
  headshotURL?: string;
  headshotZoom?: number;
  headshotPositionX?: number;
  headshotPositionY?: number;
  headshotOriginalHeight?: number;
  headshotOriginalWidth?: number;
  isHeadshotChanged?: boolean;
  companyLogoURL?: string;
  companyLogoZoom?: number;
  companyLogoPositionX?: number;
  companyLogoPositionY?: number;
  companyLogoOriginalHeight?: number;
  companyLogoOriginalWidth?: number;
  isCompanyLogoChanged?: boolean;
}

export interface PartnerHeadshotUpdateRequest {
  partnerGuid: string;
  file?: File | null;
  cropOnly?: boolean;
  remove?: boolean;
  headshotOriginalHeight?: number | null;
  headshotOriginalWidth?: number | null;
  headshotPositionX?: number | null;
  headshotPositionY?: number | null;
  headshotZoom?: number | null;
  headshotURL?: string | null;
  originalHeadshotURL?: string | null;
}

export interface PartnerCompanyLogoUpdateRequest {
  partnerGuid: string;
  file?: File | null;
  cropOnly?: boolean;
  remove?: boolean;
  companyLogoOriginalHeight?: number | null;
  companyLogoOriginalWidth?: number | null;
  companyLogoPositionX?: number | null;
  companyLogoPositionY?: number | null;
  companyLogoZoom?: number | null;
  companyLogoURL?: string | null;
  originalCompanyLogoURL?: string | null;
}

export interface AddPartnerImageResponse {
  cloudinaryUrl?: string;
}

export interface InvitePartnerResponse {
  partnerGuid: string;
  successful: boolean;
  error?: string;
}

export interface PartnerDetails {
  guid: string;
  firstName: string;
  lastName: string;
  phone: string;
  phoneNumberExt: string;
  company: string;
  title: string;
  email: string;
  accountEmail: string;
  inviteStatus: InviteStatus;
  isCobrandingActive: boolean;
  isOutsideCurrentLoPipeline: boolean;
  loanOfficerNmls: string;
}

export interface AddPartnerData {
  partnerDetails: PartnerDetails[];
  currentUserNmls: string;
}

export interface CreatePartnerRequest {
  emailAddress: string;
  type: string;
  source: string;
  firstName: string;
  lastName: string;
  title: string;
  phoneNumber: string;
  website: string;
  companyName: string;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZipCode: string;
  headShotUrl: string;
  invitedByLoanOfficerNmls: string;
  licenses: PartnerLicense[];
  headshotURL?: string;
  headshotZoom?: number;
  headshotPositionX?: number;
  headshotPositionY?: number;
  companyLogoURL?: string;
  companyLogoZoom?: number;
  companyLogoPositionX?: number;
  companyLogoPositionY?: number;
}

export interface CreatePartnerDataResponse {
  guid: string;
}

export interface NoteRequest {
  partnerGuid: string;
  loanOfficerNmls: string;
  noteGuid?: string;
  note: string;
}

@Injectable({
  providedIn: 'root',
})
export class PartnersService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dialogService = inject(DialogService);
  private readonly _httpClient = inject(HttpClient);
  private readonly _notificationService = inject(NotificationService);
  private readonly _partnerNotesQuery = inject(PartnerNotesQuery);
  private readonly _partnerNotesStore = inject(PartnerNotesDataStore);
  private readonly _routeUiService = inject(RouteUiService);
  private readonly _router = inject(Router);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _environment = inject(APP_ENV);

  private _baseUrl = this._environment.retail.apiUrl;
  private readonly _phoneNumberPipe = new PhoneNumberPipe();

  private readonly _notes = {
    bulkActionMsg:
      'Please note that this action will only occur on the selected partners currently visible in your pipeline view. If you have more pipeline results than what is currently displayed, then you will need to repeat this action on additional pages as needed.',
    activateOrDeactivate1: 'the Loan Officer connection as an available co-branding option inside their portal.',
    activateOrDeactivate2: 'Active partners can be added to loan applications via the Co-Branding section on details panels.',
    activateOrDeactivate3: 'Existing co-branded loan applications will not be impacted by this change.',
    activateOrDeactivate4: 'Co-Branding section on the details panels must be used to remove a co-branded partner from a loan application.',
  };

  addLoanPartner(partnerRequest: AssignPartnerRequest) {
    return this._httpClient.post(`${this._baseUrl}/partners/assign`, partnerRequest, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  addApplicationPartner(partnerRequest: AssignPartnerRequest) {
    return this._httpClient.post(
      `${this._baseUrl}/loanapplications/${partnerRequest.fileDataId}/partners`,
      {
        partnerGuids: [partnerRequest.partnerGuid],
        loanOfficerNmlsId: partnerRequest.loanOfficerNmlsId,
      },
      {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        responseType: 'text',
      },
    );
  }

  /**
   * Displays the single-invite confirmation modal for a partner (Quick Action).
   */
  singleInvitePartnerModal(partner: Models.PipelinePartner) {
    if (partner.inviteStatus === InviteStatus.AccountCreated) {
      this._notificationService.showToast({
        severity: 'error',
        summary: 'No Partners Invited',
        detail: INVITE_ALREADY_CREATED_DETAIL,
      });
      return;
    }

    const company = partner.company ? partner.company : '';
    const emailToShow = partner.accountEmail || partner.email || '';
    const phoneLine = partner.phone ? this._phoneNumberPipe.transform(partner.phone) : '';

    const detailsBlock = `
      <div class='p-3 partner-details'>
        <div class='partner-name mb-2'>${partner.firstName} ${partner.lastName}</div>
        ${company ? `<div>${company}</div>` : ''}
        ${emailToShow ? `<div>${emailToShow}</div>` : ''}
        ${phoneLine ? `<div>${phoneLine}</div>` : ''}
      </div>
    `;

    const modalContent = `
      <p class='mt-0 fw-bold'>Are you sure you want to invite the following ${partner.partnerType} partner?</p>
      ${detailsBlock}
      <p>An email will be sent with a link to create their portal account, and the Invite Status column will change to Account Created once partner has successfully created their portal account.</p>
    `;

    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '460px',
      styleClass: 'co-branding-modal',
      data: {
        message: modalContent,
        header: 'Invite Partner',
        icon: 'pi pi-send',
        acceptLabel: 'Invite Partner',
        rejectLabel: 'Cancel',
      },
    });

    dialogRef.onClose.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        this.inviteSinglePartner(partner.guid, partner.loanOfficerNMLS).subscribe({
          next: (resp: InvitePartnerResponse) => {
            if (resp.successful) {
              this._notificationService.showToast({
                severity: 'success',
                summary: 'Partner successfully invited:',
                detail: `${partner.partnerType}, ${partner.firstName} ${partner.lastName}`,
              });
              this._refreshPipeline();
            } else {
              this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
            }
          },
          error: () => {
            this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
          },
        });
      }
    });
  }

  /**
   * Opens the bulk invite confirmation modal for a multi-selection of partners.
   *
   * Behavior summary:
   *  - Partitions selected partners into eligible (not Account Created) and ineligible (Account Created).
   *  - If only one partner is selected, delegates to `invitePartnerModal` (single-invite path).
   *  - If all selected partners are ineligible, shows the red "No Partners Invited" toast and aborts.
   *  - Otherwise constructs and displays a confirmation modal with explanatory content.
   *  - On user acceptance, invokes `bulkInvitePartners(eligible)` and displays a success toast.
   *  - Partial failures are currently treated the same as full success
   *  - Grid refresh is intentionally deferred (external component should trigger after completion).
   *
   * @param selectedPartners Full set of partners selected in the grid at the time of action.
   */
  bulkInvitePartnersModal(selectedPartners: Models.PipelinePartner[]) {
    // Partition selection
    const eligible = selectedPartners.filter((p) => p.inviteStatus !== InviteStatus.AccountCreated);

    // All ineligible
    if (eligible.length === 0) {
      this._notificationService.showToast({
        severity: 'error',
        summary: 'No Partners Invited',
        detail: INVITE_ALREADY_CREATED_DETAIL,
      });
      return;
    }

    // Single selection path
    if (selectedPartners.length === 1) {
      this.singleInvitePartnerModal(selectedPartners[0]);
      return;
    }

    const modalContent = `
      <p>Are you sure you want to invite the selected <strong>${selectedPartners.length} partners</strong>?</p>
      <p>An email will be sent with a link to create their portal account, and the Invite Status column will change to Account Created once partners have successfully created their portal accounts.</p>
      <p><em>${this._notes.bulkActionMsg}</em></p>
    `;

    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '460px',
      data: {
        message: modalContent,
        header: 'Invite Partners',
        icon: 'pi pi-send',
        acceptLabel: 'Invite Partners',
        rejectLabel: 'Cancel',
      },
    });

    dialogRef.onClose.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        // Subscribe to progressive stream; UI can show a loading bar updated per emission.
        // Initialize spinner with starting message
        this._spinnerService.show(`Inviting 0 of ${eligible.length} partners...`);
        this.bulkInvitePartners(eligible).subscribe({
          next: (progress) => {
            // Update a loading/progress indicator (replace with real progress bar integration)
            // Example placeholder: integrate with a progress bar service or component binding.
            const invitedSoFar = progress.successful.length;
            this._spinnerService.updateMessage(`Inviting ${invitedSoFar} of ${progress.totalPartners} partners...`);

            // When complete emit final toast & refresh
            if (progress.isComplete) {
              const invitedCount = progress.successful.length;
              if (invitedCount === 0) {
                // No successful invites -> show generic error toast per requirement.
                this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
              } else {
                this._notificationService.showToast({
                  severity: 'success',
                  summary: `${invitedCount} Partners Invited`,
                  detail: INVITE_ALREADY_CREATED_DETAIL,
                });
                this._refreshPipeline();
              }
              this._spinnerService.hide();
            }
          },
          error: () => {
            this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
            this._spinnerService.hide();
          },
          complete: () => {
            // Ensure spinner is hidden in case completion triggers without isComplete (edge case)
            this._spinnerService.hide();
          },
        });
      }
    });
  }

  /**
   * Executes the bulk operation for the provided eligible partners.
   *
   * Implementation details:
   *  - Intentionally issues MANY single-partner operation HTTP POST requests IN PARALLEL (fan‑out) for fast throughput.
   *  - Concurrency is capped via mergeMap's concurrency parameter to avoid overwhelming the backend.
   *  - Each individual request failure is captured and converted into a failed entry; the overall stream does NOT error
   *    unless an unexpected outer condition occurs. This ensures partial failures still yield progressive updates.
   *  - Uses RxJS scan (instead of reduce) to emit a running accumulation after EVERY partner completes so the UI can
   *    update progress indicators (spinner text / progress bar) in real time.
   *  - The returned Observable completes after all partner requests settle; the final emission has isComplete = true.
   *
   * Why parallel instead of batching:
   *  - Provides immediate first-progress feedback (no long silent wait for a large batch to finish).
   *  - Reduces tail latency for early partners and allows UI to reflect incremental completion.
   *  - Simplifies per-partner error attribution (each request is isolated).
   *
   * @param partners Partners deemed eligible for invitation (must have a guid).
   * @returns Observable<BulkPartnersProgress> progressive emissions culminating in final aggregate.
   */
  bulkInvitePartners(partners: Models.PipelinePartner[]) {
    const apiConcurrencyLimit = 50;
    const eligiblePartners = partners.filter((p) => !!p.guid) as Models.PipelinePartner[];
    const total = eligiblePartners.length;
    if (!total) {
      return of({ successful: [], failed: [], processedPartners: 0, totalPartners: 0, isComplete: true } as BulkPartnersProgress);
    }

    // Emit progressive updates using scan (instead of reduce) so subscribers can display a loading bar.
    return from(eligiblePartners).pipe(
      mergeMap((partner) => {
        return this.inviteSinglePartner(partner.guid, partner.loanOfficerNMLS).pipe(
          catchError((err) => {
            return of({ partnerGuid: partner.guid || '', successful: false, error: err?.message || 'Unknown error' } as PartnerResponse);
          }),
        );
      }, apiConcurrencyLimit),
      scan(
        (acc: BulkPartnersProgress, resp: PartnerResponse) => {
          if (resp.successful) {
            acc.successful.push(resp.partnerGuid);
          } else {
            acc.failed.push(resp.partnerGuid);
          }
          acc.processedPartners += 1;
          acc.last = resp;
          acc.isComplete = acc.processedPartners === acc.totalPartners;
          return { ...acc };
        },
        { successful: [], failed: [], processedPartners: 0, totalPartners: total, isComplete: false } as BulkPartnersProgress,
      ),
    );
  }

  /**
   * Invites a single partner
   * Maps backend response into a simplified PartnerResponse for caller convenience.
   */
  inviteSinglePartner(partnerGuid: string, loanOfficerNmls: string) {
    partnerGuid = partnerGuid || '';
    if (!partnerGuid) {
      return of({ partnerGuid: '', successful: false, error: 'Missing partner GUID' } as PartnerResponse);
    }
    return this._httpClient
      .post<BulkPartnersResponse>(
        `${this._baseUrl}/partners/${partnerGuid}/invite`,
        { loanOfficerNmls },
        {
          context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        },
      )
      .pipe(
        map((): PartnerResponse => {
          return { partnerGuid, successful: true };
        }),
        catchError((err) => {
          const message = err?.error?.message || err?.message || 'Unknown error';
          return of({ partnerGuid, successful: false, error: message } as PartnerResponse);
        }),
      );
  }

  bulkActivateOrDeactivatePartnersModal(selectedPartners: Models.PipelinePartner[], isActivate: boolean) {
    if (!selectedPartners?.length) {
      return;
    }

    // Single selection path
    if (selectedPartners.length === 1) {
      this.showSingleActivateOrDeactivateCoBrandingModal(isActivate, selectedPartners[0]);
      return;
    }

    const modalContent = `
      <p>Are you sure you want to ${isActivate ? 'activate' : 'deactivate'} co-branding on the selected <strong>${selectedPartners.length} partners</strong>?</p>
      <p>This action will change Co-Branding to ${isActivate ? 'Active' : 'Inactive'} which will cause these partners to
      ${isActivate ? 'see' : 'no longer see'} ${this._notes.activateOrDeactivate1}</p>
      ${isActivate ? '<p>Active partners can be added to loan applications via the Co-Branding section on details panels.</p>' : ''}
      ${!isActivate ? `<p class="mb-0 warning-text fw-bold">${this._notes.activateOrDeactivate3}</p>` : ''}
      ${!isActivate ? `<p class="m-0">${this._notes.activateOrDeactivate4}</p>` : ''}
      <p><em>${this._notes.bulkActionMsg}</em></p>
    `;

    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      styleClass: 'activate-co-branding-modal',
      data: {
        message: modalContent,
        header: `${isActivate ? 'Activate' : 'Deactivate'} Partner Co-Branding`,
        icon: `pi ${isActivate ? 'pi-user-plus' : 'pi-user-minus'}`,
        acceptLabel: `${isActivate ? 'Activate' : 'Deactivate'} Partners`,
        rejectLabel: 'Cancel',
      },
    });

    dialogRef.onClose.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        const cobrandingRequests: CoBrandingRequest[] = selectedPartners.map((p) => {
          return { partnerGuid: p.guid, loanOfficerNmls: p.loanOfficerNMLS };
        });
        this.activateOrDeactivateCoBranding(isActivate, cobrandingRequests)
          .pipe(
            catchError(() => {
              this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
              return EMPTY;
            }),
          )
          .subscribe(() => {
            this._notificationService.showToast({
              severity: 'success',
              summary: `${selectedPartners.length} Partners ${isActivate ? 'Activated' : 'Deactivated'}`,
            });
            this._refreshPipeline();
          });
      }
    });
  }

  /**
   * Changes (reassigns) an existing loan partner to a new one.
   */
  changeLoanPartner(request: ChangePartnerRequest) {
    return this._httpClient.post(`${this._baseUrl}/partners/reassign`, request, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  /**
   * Changes (replaces) an existing application partner with a new one.
   */
  changeApplicationPartner(request: ChangePartnerRequest) {
    const { fileDataId, oldPartnerGuid, newPartnerGuid, loanOfficerNmlsId } = request;
    return this._httpClient.put(
      `${this._baseUrl}/loanapplications/${fileDataId}/partners`,
      {
        oldPartnerGuid,
        newPartnerGuid,
        loanOfficerNmlsId,
      },
      {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        responseType: 'text',
      },
    );
  }

  /**
   * Remove a loan partner (co-branded REA)
   */
  removeLoanPartner(partnerRequest: AssignPartnerRequest) {
    return this._httpClient.post(`${this._baseUrl}/partners/remove`, partnerRequest, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  /**
   * Removes an application partner (co-branded REA) via new DELETE endpoint.
   */
  removeApplicationPartner(partnerRequest: AssignPartnerRequest) {
    return this._httpClient.delete(`${this._baseUrl}/loanapplications/${partnerRequest.fileDataId}/partners`, {
      body: {
        partnerGuids: [partnerRequest.partnerGuid],
        loanOfficerNmlsId: partnerRequest.loanOfficerNmlsId,
      },
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  /**
   * Retrieves all partners for the pipeline.
   */
  getPartners(isCobrandingActive: boolean): Observable<PartnerData[]> {
    const options: { context: HttpContext; params?: Record<string, any> } = {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: { isCobrandingActive },
    };
    return this._httpClient.get<PartnerData[]>(`${this._baseUrl}/partners/pipeline`, options);
  }

  /**
   * Retrieves all partners for a given NMLS ID.
   */
  getPartnersByNmlsId(nmlsid: string): Observable<PartnerData[]> {
    const options: { context: HttpContext; params?: Record<string, any> } = {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: { loanOfficerNMLS: nmlsid },
    };
    return this._httpClient.get<PartnerData[]>(`${this._baseUrl}/partners/search`, options);
  }

  showSingleActivateOrDeactivateCoBrandingModal(isActivate: boolean, partner: Models.PipelinePartner) {
    const companyAndTitle = `<p class='m-0'>${partner.company ? partner.company + (partner.title ? ', ' : '') : ''}${partner.title ?? ''}</p>`;
    const email = partner.email ? `<p class='m-0'>${partner.email}</p>` : '';
    const phone = partner.phone ? `<p class='m-0'>${this._phoneNumberPipe.transform(partner.phone)}</p>` : '';
    const modalContent = `
          <p class='mt-0 fw-bold'>Are you sure you want to ${isActivate ? 'activate' : 'deactivate'} the following ${partner.partnerType} partner?</p>
          <div class='p-3 pt-0 partner-details'>
          <p class='fw-bold'>${partner.firstName} ${partner.lastName}</p>
          ${companyAndTitle ? companyAndTitle : ''}
          ${email ? email : ''}
          ${phone ? phone : ''}
          </div>
          <p>This action will change Co-Branding to ${isActivate ? 'Active' : 'Inactive'} which will cause this partner to
          ${isActivate ? 'see' : 'no longer see'} ${this._notes.activateOrDeactivate1}</p>
          ${isActivate ? `<p class="mb-0">${this._notes.activateOrDeactivate2}</p>` : ''}
          ${!isActivate ? '<p class="mb-0 warning-text fw-bold">Existing co-branded loan applications will not be impacted by this change.</p>' : ''}
          ${!isActivate ? `<p class="m-0">${this._notes.activateOrDeactivate4}</p>` : ''}
          `;
    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      styleClass: 'activate-co-branding-modal',
      data: {
        message: modalContent,
        header: `${isActivate ? 'Activate' : 'Deactivate'} Partner Co-Branding`,
        icon: `pi ${isActivate ? 'pi-user-plus' : 'pi-user-minus'}`,
        acceptLabel: `${isActivate ? 'Activate' : 'Deactivate'} Partner`,
        rejectLabel: 'Cancel',
      },
    });

    dialogRef.onClose.subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        this.activateOrDeactivateCoBranding(isActivate, [{ partnerGuid: partner.guid, loanOfficerNmls: partner.loanOfficerNMLS }])
          .pipe(
            catchError(() => {
              this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
              return EMPTY;
            }),
          )
          .subscribe(() => {
            this._notificationService.showToast({
              severity: 'success',
              summary: `Partner successfully ${isActivate ? 'activated' : 'deactivated'}:`,
              detail: `${partner.partnerType}, ${partner.firstName} ${partner.lastName}`,
            });
            this._refreshPipeline();
          });
      }
    });
  }

  activateOrDeactivateCoBranding(isActivate: boolean, requests: CoBrandingRequest[]) {
    const endpoint = isActivate ? 'activate' : 'deactivate';
    return this._httpClient.post(`${this._baseUrl}/partners/cobranding/${endpoint}`, requests, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  addPartner(partner: CreatePartnerRequest) {
    return this._httpClient
      .post<CreatePartnerDataResponse>(`${this._baseUrl}/partners/create`, partner, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(
        catchError(() => {
          this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
          return of({ guid: null } as CreatePartnerDataResponse);
        }),
      );
  }

  updatePartner(partnerGuid: string, partner: EditPartnerData) {
    return this._httpClient.patch(`${this._baseUrl}/partners/${partnerGuid}`, partner, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      responseType: 'text',
    });
  }

  updatePartnerHeadshot(request: PartnerHeadshotUpdateRequest): Observable<AddPartnerImageResponse | void> {
    if (!request?.partnerGuid) {
      return of(null);
    }

    if (request.remove) {
      return this._deletePartnerHeadshot(request.partnerGuid);
    }

    // This should be handled by the primary partner PATCH API call
    if (request.cropOnly && !request.file) {
      return of(null);
    }

    return this._uploadPartnerHeadshot(request);
  }

  /**
   * Uploads a partner's headshot image to the server.
   * @param request The request containing the partner GUID and image data.
   * @returns An observable of the upload response.
   */
  private _uploadPartnerHeadshot(request: PartnerHeadshotUpdateRequest): Observable<AddPartnerImageResponse> {
    return this._uploadPartnerImage({
      endpoint: 'headshot',
      partnerGuid: request.partnerGuid,
      file: request.file,
      zoom: request.headshotZoom,
      positionX: request.headshotPositionX,
      positionY: request.headshotPositionY,
      cropOnly: request.cropOnly,
    });
  }

  /**
   * Deletes a partner's headshot image from the server.
   * @param partnerGuid The GUID of the partner whose headshot is to be deleted.
   * @returns An observable of the delete operation.
   */
  private _deletePartnerHeadshot(partnerGuid: string): Observable<void> {
    const params = new HttpParams().set('partnerGuid', partnerGuid);

    return this._httpClient.delete<void>(`${this._baseUrl}/partners/headshot`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params,
    });
  }

  /**
   * Updates a partner's company logo.
   * @param request The request containing the partner GUID and image data.
   * @returns An observable of the update response.
   */
  updatePartnerCompanyLogo(request: PartnerCompanyLogoUpdateRequest): Observable<AddPartnerImageResponse | void> {
    if (!request?.partnerGuid) {
      return of(null);
    }

    if (request.remove) {
      return this._deletePartnerCompanyLogo(request.partnerGuid);
    }

    // This should be handled by the primary partner PATCH API call
    if (request.cropOnly && !request.file) {
      return of(null);
    }

    return this._uploadPartnerCompanyLogo(request);
  }

  private _uploadPartnerCompanyLogo(request: PartnerCompanyLogoUpdateRequest): Observable<AddPartnerImageResponse> {
    return this._uploadPartnerImage({
      endpoint: 'companylogo',
      partnerGuid: request.partnerGuid,
      file: request.file,
      zoom: request.companyLogoZoom,
      positionX: request.companyLogoPositionX,
      positionY: request.companyLogoPositionY,
      cropOnly: request.cropOnly,
    });
  }

  private _uploadPartnerImage(config: {
    endpoint: 'headshot' | 'companylogo';
    partnerGuid: string;
    file?: File | null;
    zoom?: number | null;
    positionX?: number | null;
    positionY?: number | null;
    cropOnly?: boolean;
  }): Observable<AddPartnerImageResponse> {
    const formData = new FormData();

    if (config.file) {
      const fileName = config.file.name || 'file';
      formData.append('file', config.file, fileName);
    }

    if (!isNullish(config.zoom)) {
      formData.append('zoom', config.zoom.toString());
    }

    if (!isNullish(config.positionX)) {
      formData.append('positionX', config.positionX.toString());
    }

    if (!isNullish(config.positionY)) {
      formData.append('positionY', config.positionY.toString());
    }

    if (config.cropOnly !== undefined) {
      formData.append('cropOnly', String(config.cropOnly));
    }

    const params = new HttpParams().set('partnerGuid', config.partnerGuid);

    return this._httpClient.post<AddPartnerImageResponse>(`${this._baseUrl}/partners/${config.endpoint}`, formData, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params,
    });
  }

  private _deletePartnerCompanyLogo(partnerGuid: string): Observable<void> {
    const params = new HttpParams().set('partnerGuid', partnerGuid);

    return this._httpClient.delete<void>(`${this._baseUrl}/partners/companylogo`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params,
    });
  }

  getPartnerCobrandedLink(partnerId: string, loanOfficerNMLS: string): Observable<Models.PartnerCobrandedLinkResponse> {
    return this._httpClient.get<Models.PartnerCobrandedLinkResponse>(`${this._baseUrl}/partners/employee`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      params: {
        partnerId,
        loanOfficerNMLS,
      },
    });
  }

  showAddPartnerExistingAccountModal(partnerData: PartnerDetails) {
    const companyAndTitle = `<p class='m-0'>${partnerData.company ? partnerData.company + (partnerData.title ? ', ' : '') : ''}${partnerData.title ?? ''}</p>`;
    const email = partnerData.email ? `<p class='m-0'>${partnerData.email}</p>` : '';
    const phone = partnerData.phone ? `<p class='m-0'>${this._phoneNumberPipe.transform(partnerData.phone)}</p>` : '';
    const modalContent = `
      <div class="mb-4 fw-semibold">Email found as an existing account. Is this the Real Estate Agent partner you wish to add?</div>
      <div class="p-3 pt-0 partner-details">
        <p class='fw-bold fs-5 mb-2'>${partnerData.firstName} ${partnerData.lastName}</p>
        ${companyAndTitle}
        ${email}
        ${phone}
      </div>
    `;

    return this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      styleClass: 'add-partner-view-modal',
      data: {
        message: modalContent,
        header: 'Add Partner',
        icon: 'pi pi-user-plus',
        acceptLabel: 'Yes, Add Partner',
        rejectLabel: 'No, Try Different Email',
      },
    });
  }

  showAddPartnerNewAccountModal(accountEmail: string, partnerType: string, loanOfficerNmls: string, routeUiService) {
    const email = `<p class='m-0'>${accountEmail}</p>`;
    const modalContent = `
      <div class="mb-4 fw-semibold">No existing partner found with this email. Do you want to invite this partner?</div>
      <div class="p-3 partner-details">
        ${email}
      </div>
    `;

    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      styleClass: 'add-partner-view-modal',
      data: {
        message: modalContent,
        header: `Add Partner`,
        icon: `pi pi-user-plus`,
        acceptLabel: `Yes, Invite Partner`,
        rejectLabel: 'No, Try Different Email',
      },
    });

    dialogRef.onClose.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        this._showInvitePartnerDrawer(accountEmail, partnerType, loanOfficerNmls, routeUiService);
      } else {
        this._dialogService.open(AddPartnerModalComponent, {
          closable: true,
          header: 'Add Partner',
          width: '512px',
          data: {
            loanOfficerNmls,
            partnerType,
            accountEmail,
          },
        });
        addIconClassToModalHeader('pi pi-user-plus');
      }
    });
  }

  private _showInvitePartnerDrawer(accountEmail: string, title: string, loanOfficerNMLS: string, routeUiService: RouteUiService) {
    const partner = {
      accountEmail,
      title,
      loanOfficerNMLS,
    };
    showPartnersDrawer(partner, true, routeUiService, this._notificationService);
  }

  getPartnerInfo(email: string, nmlsId: string) {
    const urlEncodedEmail = encodeURIComponent(email);
    const urlEncodedNmlsId = encodeURIComponent(nmlsId);
    return this._httpClient.get(`${this._baseUrl}/partners/info?emailAddress=${urlEncodedEmail}&loanOfficerNmlsId=${urlEncodedNmlsId}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  /**
   * Re-navigates to the pipeline route to force a data reload, preserving any active grid filter.
   * Mirrors the logic used by `PaginatorComponent.refreshGrid()`.
   */
  private _refreshPipeline() {
    const activeFilter = this._routeUiService.activeGridFilter$.value;
    if (activeFilter) {
      navigateWithRefresh(this._router, '/pipeline', { queryParams: { searchFilter: activeFilter } });
      return;
    }
    navigateWithRefresh(this._router, '/pipeline');
  }

  getPartnerNote(partnerGuid: string): Observable<PartnerNote[]> {
    return this._sessionQuery
      .select((s) => s.nmlsid)
      .pipe(
        switchMap((nmlsId) => {
          return this._httpClient
            .get<PartnerNote[]>(`${this._environment.retail.apiUrl}/partners/note?partnerGUID=${partnerGuid}&loanOfficerNMLS=${nmlsId}`, {
              context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
            })
            .pipe(
              catchError(() => {
                this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
                return EMPTY;
              }),
              tap((notes) => {
                let scratchpadNote;
                if (notes && notes.length > 0) {
                  // Scratchpad will be enhanced in future to support multiple notes.
                  // For now the scratchpad only supports one note so use note at index 0.
                  scratchpadNote = notes[0];
                } else {
                  scratchpadNote = {
                    dateInserted: null,
                    dateUpdated: null,
                    guid: null,
                    insertedBy: '',
                    loanOfficerNmls: nmlsId,
                    note: '',
                    partnerGuid: partnerGuid,
                    updatedBy: '',
                  } as PartnerNote;
                }

                this._partnerNotesStore.update(scratchpadNote);
                this._partnerNotesStore.preInitialize();
                this._partnerNotesQuery.dirtyChecker2.dirtyChecker.setHead();
              }),
            );
        }),
      );
  }

  upsertPartnerNote(): Observable<any> {
    const note = this._partnerNotesQuery.getValue();
    const request: NoteRequest = {
      partnerGuid: note.partnerGuid,
      loanOfficerNmls: note.loanOfficerNmls,
      note: note.note,
    };

    let resp$;
    if (note?.guid) {
      // Update
      request.noteGuid = note.guid;
      resp$ = this._httpClient.patch(`${this._environment.retail.apiUrl}/partners/note`, request, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      });
    } else {
      // Create
      resp$ = this._httpClient.post(`${this._environment.retail.apiUrl}/partners/note`, request, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      });
    }

    return resp$.pipe(
      catchError(() => {
        this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
        return EMPTY;
      }),
    );
  }
}
